import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export interface TopbarTab {
  id: string;
  label: string;
  active?: boolean;
  onSelect?: () => void;
}

interface TopbarTabsState {
  tabs: TopbarTab[];
  setTabs: (tabs: TopbarTab[]) => void;
}

const TopbarTabsContext = createContext<TopbarTabsState | null>(null);

export function TopbarTabsProvider({ children }: { children: ReactNode }) {
  const [tabs, setTabs] = useState<TopbarTab[]>([]);
  const value = useMemo(() => ({ tabs, setTabs }), [tabs]);
  return <TopbarTabsContext.Provider value={value}>{children}</TopbarTabsContext.Provider>;
}

export function useTopbarTabs(): TopbarTab[] {
  const ctx = useContext(TopbarTabsContext);
  return ctx?.tabs ?? [];
}

// Pages call this with their tab list. Cleared automatically on unmount so
// navigating away doesn't leave stale tabs in the bar.
export function useSetTopbarTabs(tabs: TopbarTab[]) {
  const ctx = useContext(TopbarTabsContext);
  const serialized = JSON.stringify(tabs.map(({ id, label, active }) => ({ id, label, active })));
  useEffect(() => {
    ctx?.setTabs(tabs);
    return () => ctx?.setTabs([]);
    // Re-publish only when the visible shape of the tabs changes, not on every
    // render (which would happen if pages pass fresh onSelect closures).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized]);
}
